import { useState, useEffect, useRef, useCallback } from 'react';

export const useGridLayout = (isLoading, rowsLength) => {
  const [isFiltersFixed, setIsFiltersFixed] = useState(false);
  const [gridRightEdge, setGridRightEdge] = useState(16);
  const gridContainerRef = useRef(null);

  // Shared function to calculate grid right edge
  const calculateGridRightEdge = useCallback(() => {
    if (gridContainerRef.current) {
      const rect = gridContainerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const gridRight = rect.right;
      // Calculate distance from viewport right edge to grid right edge
      const rightOffset = viewportWidth - gridRight;
      setGridRightEdge(Math.max(16, rightOffset)); // Minimum 16px from edge
    }
  }, []);

  // Calculate grid right edge when grid container is available
  useEffect(() => {
    if (gridContainerRef.current) {
      // Calculate on mount and after a short delay to ensure layout is complete
      calculateGridRightEdge();
      const timeoutId = setTimeout(calculateGridRightEdge, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, rowsLength, calculateGridRightEdge]); // Recalculate when data loads

  // Handle scroll to make filters fixed at bottom after scrolling past threshold
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 50; // Scroll threshold in pixels
      const scrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      setIsFiltersFixed(scrollY > scrollThreshold);
      calculateGridRightEdge();
    };

    const handleResize = () => {
      calculateGridRightEdge();
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    // Check initial scroll position and calculate grid edge
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateGridRightEdge]);

  return {
    isFiltersFixed,
    gridRightEdge,
    gridContainerRef
  };
};
